import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useApp } from '../store';

const PAGE_SIZE = 20;

const TABS = [
  { id: 'daily', label: '每日数据' },
  { id: 'users', label: '用户列表' },
  { id: 'game', label: '游戏查询' },
  { id: 'tasks', label: '任务设置' },
  { id: 'bots', label: '机器人' },
  { id: 'notice', label: '跑马灯公告' },
  { id: 'redpack', label: '房卡红包' },
];

const ROOM_STATUS = ['WAITING', 'READYING', 'COUNTDOWN', 'RUNNING', 'SETTLING', 'ENDED'];
const LEDGER_TYPES = [
  'daily_card',
  'daily_points',
  'enter_room_card',
  'enter_room_pts',
  'settle_win',
  'share_win',
  'settle_bust',
  'refund',
  'recharge',
  'card_sale_income',
  'card_purchase',
  'invite_card_claim',
  'withdraw_usdt',
];

const EMPTY_TASK = {
  id: null,
  iconUrl: '/ansem-logo.webp',
  description: '',
  rewardText: '',
  targetUrl: '',
  sortOrder: 100,
  isActive: true,
};
const BOT_LEVEL_LABELS = {
  1: '初级场',
  2: '中级场',
  3: '高级场',
};

function asNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function fmtInt(value) {
  return asNumber(value).toLocaleString('zh-CN');
}

function fmtDecimal(value, digits = 6) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '0');
  return n.toLocaleString('zh-CN', { maximumFractionDigits: digits });
}

function fmtDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', { hour12: false });
}

function errorMessage(error, fallback = '操作失败') {
  return `${error || fallback}`;
}

function amountOf(row) {
  return row.amount_decimal ?? row.amount;
}

function directionText(value) {
  if (value === 1) return '做多';
  if (value === -1) return '做空';
  return '-';
}

function EmptyState({ loading, text = '暂无数据' }) {
  return <div className="admin-empty">{loading ? '加载中...' : text}</div>;
}

export default function Admin() {
  const nav = useNavigate();
  const { user } = useApp();
  const [active, setActive] = useState('daily');
  const [notice, setNotice] = useState('');
  const [accessError, setAccessError] = useState('');

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [dailyDays, setDailyDays] = useState(14);
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);

  const [userQ, setUserQ] = useState('');
  const [userStatus, setUserStatus] = useState('all');
  const [userPage, setUserPage] = useState(1);
  const [userRows, setUserRows] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);

  const [gameMode, setGameMode] = useState('records');
  const [gamePage, setGamePage] = useState(1);
  const [gameRows, setGameRows] = useState([]);
  const [gameTotal, setGameTotal] = useState(0);
  const [gameLoading, setGameLoading] = useState(false);
  const [gameFilters, setGameFilters] = useState({
    userId: '',
    roomId: '',
    level: '',
    status: '',
    type: '',
    asset: '',
    from: '',
    to: '',
  });

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskForm, setTaskForm] = useState({ ...EMPTY_TASK });
  const [taskSaving, setTaskSaving] = useState(false);
  const [botCounts, setBotCounts] = useState({ 1: '0', 2: '0', 3: '0' });
  const [botMax, setBotMax] = useState(1000);
  const [botLoading, setBotLoading] = useState(false);
  const [botSaving, setBotSaving] = useState(false);
  const [noticeText, setNoticeText] = useState('');
  const [noticeUrl,  setNoticeUrl]  = useState('');
  const [noticeSaving, setNoticeSaving] = useState(false);

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    if (active === 'daily') loadDaily();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, dailyDays]);

  useEffect(() => {
    if (active === 'users') loadUsers(userPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, userPage, userStatus]);

  useEffect(() => {
    if (active === 'game') loadGame(gamePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, gamePage, gameMode]);

  useEffect(() => {
    if (active === 'tasks') loadTasks();
  }, [active]);

  useEffect(() => {
    if (active === 'bots') loadBots();
  }, [active]);

  useEffect(() => {
    if (active === 'notice') loadNotice();
  }, [active]);

  async function loadSummary() {
    setSummaryLoading(true);
    try {
      const data = await API.get('/api/admin/summary');
      setSummary(data);
      setAccessError('');
    } catch (e) {
      setAccessError('无管理员权限或登录已过期');
    } finally {
      setSummaryLoading(false);
    }
  }

  async function loadDaily() {
    setDailyLoading(true);
    try {
      const data = await API.get('/api/admin/daily', { params: { days: dailyDays } });
      setDailyRows(Array.isArray(data.list) ? data.list : []);
      setNotice('');
    } catch (e) {
      setNotice(errorMessage(e, '每日数据加载失败'));
    } finally {
      setDailyLoading(false);
    }
  }

  async function loadUsers(page = userPage) {
    setUsersLoading(true);
    try {
      const data = await API.get('/api/admin/users', {
        params: { page, pageSize: PAGE_SIZE, q: userQ, status: userStatus },
      });
      setUserRows(Array.isArray(data.list) ? data.list : []);
      setUserTotal(asNumber(data.total));
      setNotice('');
    } catch (e) {
      setNotice(errorMessage(e, '用户列表加载失败'));
    } finally {
      setUsersLoading(false);
    }
  }

  async function patchUser(row, patch) {
    try {
      await API.patch(`/api/admin/users/${row.id}`, patch);
      setNotice('用户状态已更新');
      await Promise.all([loadUsers(userPage), loadSummary()]);
    } catch (e) {
      setNotice(errorMessage(e, '用户状态更新失败'));
    }
  }

  async function loadGame(page = gamePage) {
    setGameLoading(true);
    try {
      const endpoint = gameMode === 'records'
        ? '/api/admin/game/records'
        : '/api/admin/game/ledger';
      const data = await API.get(endpoint, {
        params: { page, pageSize: PAGE_SIZE, ...gameFilters },
      });
      setGameRows(Array.isArray(data.list) ? data.list : []);
      setGameTotal(asNumber(data.total));
      setNotice('');
    } catch (e) {
      setNotice(errorMessage(e, '游戏数据加载失败'));
    } finally {
      setGameLoading(false);
    }
  }

  async function loadTasks() {
    setTasksLoading(true);
    try {
      const data = await API.get('/api/admin/tasks');
      setTasks(Array.isArray(data.list) ? data.list : []);
      setNotice('');
    } catch (e) {
      setNotice(errorMessage(e, '任务列表加载失败'));
    } finally {
      setTasksLoading(false);
    }
  }

  async function loadBots() {
    setBotLoading(true);
    try {
      const data = await API.get('/api/admin/bots');
      const next = { 1: '0', 2: '0', 3: '0' };
      for (const row of data.list || []) next[Number(row.level)] = String(asNumber(row.botCount));
      setBotCounts(next);
      setBotMax(asNumber(data.max, 1000));
      setNotice('');
    } catch (e) {
      setNotice(errorMessage(e, '机器人配置加载失败'));
    } finally {
      setBotLoading(false);
    }
  }

  async function loadNotice() {
    try {
      const data = await API.get('/api/admin/notice');
      setNoticeText(data.text || '');
      setNoticeUrl(data.url || '');
    } catch (e) {
      setNotice(errorMessage(e, '公告加载失败'));
    }
  }

  async function saveNotice() {
    setNoticeSaving(true);
    try {
      await API.put('/api/admin/notice', { text: noticeText, url: noticeUrl });
      setNotice('保存成功');
      setTimeout(() => setNotice(''), 2000);
    } catch (e) {
      setNotice(errorMessage(e, '保存失败'));
    } finally {
      setNoticeSaving(false);
    }
  }

  function setBotCount(level, value) {
    const text = String(value ?? '');
    if (text === '') {
      setBotCounts(prev => ({ ...prev, [level]: '' }));
      return;
    }
    const n = Number.parseInt(text, 10);
    if (!Number.isFinite(n)) return;
    setBotCounts(prev => ({ ...prev, [level]: String(Math.max(0, Math.min(botMax, n))) }));
  }

  function setFilter(name, value) {
    setGameFilters(prev => ({ ...prev, [name]: value }));
  }

  function editTask(row) {
    setTaskForm({
      id: row.id,
      iconUrl: row.icon_url || '/ansem-logo.webp',
      description: row.description || '',
      rewardText: row.reward_text || '',
      targetUrl: row.target_url || '',
      sortOrder: row.sort_order ?? 100,
      isActive: !!row.is_active,
    });
  }

  function resetTaskForm() {
    setTaskForm({ ...EMPTY_TASK });
  }

  async function saveTask(e) {
    e.preventDefault();
    setTaskSaving(true);
    const payload = {
      iconUrl: taskForm.iconUrl,
      description: taskForm.description,
      rewardText: taskForm.rewardText,
      targetUrl: taskForm.targetUrl,
      sortOrder: Number(taskForm.sortOrder),
      isActive: !!taskForm.isActive,
    };
    try {
      if (taskForm.id) await API.put(`/api/admin/tasks/${taskForm.id}`, payload);
      else await API.post('/api/admin/tasks', payload);
      setNotice(taskForm.id ? '任务已保存' : '任务已创建');
      resetTaskForm();
      await Promise.all([loadTasks(), loadSummary()]);
    } catch (err) {
      setNotice(errorMessage(err, '任务保存失败'));
    } finally {
      setTaskSaving(false);
    }
  }

  async function toggleTask(row) {
    try {
      await API.put(`/api/admin/tasks/${row.id}`, {
        iconUrl: row.icon_url || '/ansem-logo.webp',
        description: row.description,
        rewardText: row.reward_text || '',
        targetUrl: row.target_url,
        sortOrder: row.sort_order,
        isActive: !row.is_active,
      });
      setNotice('任务状态已更新');
      await Promise.all([loadTasks(), loadSummary()]);
    } catch (e) {
      setNotice(errorMessage(e, '任务状态更新失败'));
    }
  }

  async function deleteTask(row) {
    if (!window.confirm(`确认删除任务 #${row.id}？`)) return;
    try {
      await API.delete(`/api/admin/tasks/${row.id}`);
      if (taskForm.id === row.id) resetTaskForm();
      setNotice('任务已删除');
      await Promise.all([loadTasks(), loadSummary()]);
    } catch (e) {
      setNotice(errorMessage(e, '任务删除失败'));
    }
  }

  async function saveBots(e) {
    e.preventDefault();
    setBotSaving(true);
    const counts = {};
    for (const level of [1, 2, 3]) {
      const n = Number.parseInt(String(botCounts[level] || '0'), 10);
      counts[level] = Math.max(0, Math.min(botMax, Number.isFinite(n) ? n : 0));
    }
    try {
      const data = await API.put('/api/admin/bots', { counts });
      const next = { 1: '0', 2: '0', 3: '0' };
      for (const row of data.list || []) next[Number(row.level)] = String(asNumber(row.botCount));
      setBotCounts(next);
      setNotice('机器人配置已保存');
    } catch (err) {
      setNotice(errorMessage(err, '机器人配置保存失败'));
    } finally {
      setBotSaving(false);
    }
  }

  const totalUserPages = Math.max(1, Math.ceil(userTotal / PAGE_SIZE));
  const totalGamePages = Math.max(1, Math.ceil(gameTotal / PAGE_SIZE));

  const S = `
    .admin-root{min-height:100vh;min-height:100dvh;background:#0e1116;color:#eef3f8;font-family:Inter,Arial,sans-serif;}
    .admin-shell{max-width:1280px;margin:0 auto;padding:26px 22px 48px;}
    .admin-top{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;margin-bottom:18px;}
    .admin-title{font-size:24px;font-weight:850;line-height:1.1;}
    .admin-sub{font-size:13px;color:#8f9cad;margin-top:6px;}
    .admin-actions{display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end;}
    .admin-btn{height:36px;padding:0 13px;border-radius:8px;border:1px solid #2b3645;background:#18212c;color:#e8eef6;font-size:13px;font-weight:700;cursor:pointer;}
    .admin-btn:hover{border-color:#3b82f6;background:#1d2a3a;}
    .admin-btn.primary{background:#2f80ed;border-color:#2f80ed;color:#fff;}
    .admin-btn.warn{background:#3a2024;border-color:#6d3038;color:#ffb4bc;}
    .admin-btn.success{background:#173529;border-color:#2e7d5b;color:#b9f6d1;}
    .admin-btn:disabled{opacity:.45;cursor:not-allowed;}
    .admin-tabs{display:flex;gap:6px;overflow:auto;border-bottom:1px solid #252f3d;margin-bottom:18px;}
    .admin-tab{min-width:96px;height:42px;padding:0 14px;border:0;border-bottom:2px solid transparent;background:transparent;color:#91a0b3;font-weight:800;cursor:pointer;}
    .admin-tab.active{color:#fff;border-bottom-color:#f5c518;}
    .admin-metrics{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:10px;margin-bottom:18px;}
    .admin-metric{background:#151b24;border:1px solid #253140;border-radius:8px;padding:12px;min-height:78px;}
    .admin-metric-label{font-size:12px;color:#8f9cad;margin-bottom:8px;}
    .admin-metric-value{font-size:22px;font-weight:850;line-height:1.1;color:#fff;font-family:'Share Tech Mono',monospace;}
    .admin-panel{background:#131922;border:1px solid #243041;border-radius:8px;overflow:hidden;}
    .admin-panel-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px;border-bottom:1px solid #243041;}
    .admin-panel-title{font-size:16px;font-weight:850;color:#fff;}
    .admin-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
    .admin-field{display:flex;flex-direction:column;gap:5px;min-width:130px;}
    .admin-field label{font-size:12px;color:#8f9cad;font-weight:700;}
    .admin-input,.admin-select{height:36px;border-radius:8px;border:1px solid #2b3645;background:#0f141c;color:#edf3f9;padding:0 10px;font-size:13px;outline:none;}
    .admin-input:focus,.admin-select:focus{border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.16);}
    .admin-check{display:flex;align-items:center;gap:8px;height:36px;color:#dbe5ef;font-size:13px;font-weight:750;}
    .admin-table-wrap{width:100%;overflow:auto;}
    .admin-table{width:100%;min-width:960px;border-collapse:collapse;}
    .admin-table th,.admin-table td{padding:11px 12px;border-bottom:1px solid #202a38;text-align:left;vertical-align:middle;font-size:13px;white-space:nowrap;}
    .admin-table th{color:#8f9cad;background:#101720;font-size:12px;font-weight:850;position:sticky;top:0;z-index:1;}
    .admin-table td{color:#e7edf4;}
    .admin-table tr:hover td{background:#171f2a;}
    .admin-user{display:flex;flex-direction:column;gap:2px;min-width:180px;}
    .admin-user strong{font-size:13px;color:#fff;}
    .admin-user span{font-size:12px;color:#8f9cad;}
    .admin-badge{display:inline-flex;align-items:center;height:24px;padding:0 8px;border-radius:999px;font-size:12px;font-weight:800;background:#222b38;color:#aebccd;}
    .admin-badge.green{background:#143326;color:#b8f7d0;}
    .admin-badge.red{background:#3a2024;color:#ffb4bc;}
    .admin-badge.gold{background:#342d14;color:#f6da69;}
    .admin-badge.blue{background:#142945;color:#b9d8ff;}
    .admin-muted{color:#8f9cad;}
    .admin-empty{padding:36px 16px;color:#8f9cad;text-align:center;font-size:14px;}
    .admin-notice{margin-bottom:12px;padding:10px 12px;border-radius:8px;background:#182534;border:1px solid #2d4056;color:#dceafe;font-size:13px;}
    .admin-pager{display:flex;align-items:center;justify-content:flex-end;gap:10px;padding:12px 16px;border-top:1px solid #243041;color:#9caaba;font-size:13px;}
    .admin-segment{display:flex;align-items:center;padding:3px;background:#0e141d;border:1px solid #2b3645;border-radius:8px;}
    .admin-segment button{height:30px;border:0;border-radius:6px;background:transparent;color:#8f9cad;padding:0 10px;font-weight:800;cursor:pointer;}
    .admin-segment button.active{background:#253246;color:#fff;}
    .admin-task-grid{display:grid;grid-template-columns:360px minmax(0,1fr);gap:12px;padding:12px;}
    .admin-bot-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;padding:12px;}
    .admin-bot-card{border:1px solid #243041;border-radius:8px;padding:14px;background:#101720;}
    .admin-bot-title{font-size:15px;font-weight:850;color:#fff;margin-bottom:6px;}
    .admin-bot-desc{font-size:12px;color:#8f9cad;line-height:1.5;margin-bottom:12px;}
    .admin-form-panel{border:1px solid #243041;border-radius:8px;padding:14px;background:#101720;}
    .admin-form-title{font-size:15px;font-weight:850;margin-bottom:12px;}
    .admin-form{display:grid;gap:11px;}
    .admin-form textarea{min-height:76px;padding:9px 10px;resize:vertical;line-height:1.4;}
    .admin-row-actions{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
    .admin-kpi-pos{color:#4ade80;font-family:'Share Tech Mono',monospace;}
    .admin-kpi-neg{color:#fb7185;font-family:'Share Tech Mono',monospace;}
    .admin-access{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0e1116;color:#eaf1f8;padding:24px;}
    .admin-access-box{width:min(420px,100%);border:1px solid #273245;border-radius:8px;background:#141b25;padding:22px;}
    @media (max-width:980px){
      .admin-shell{padding:18px 12px 36px;}
      .admin-top{flex-direction:column;}
      .admin-actions{justify-content:flex-start;}
      .admin-metrics{grid-template-columns:repeat(2,minmax(0,1fr));}
      .admin-task-grid{grid-template-columns:1fr;}
      .admin-bot-grid{grid-template-columns:1fr;}
    }
    @media (max-width:560px){
      .admin-metrics{grid-template-columns:1fr;}
      .admin-panel-head{align-items:flex-start;flex-direction:column;}
      .admin-toolbar{width:100%;}
      .admin-field{min-width:100%;flex:1;}
      .admin-btn{width:auto;}
    }
  `;

  if (summaryLoading) {
    return (
      <div className="admin-access">
        <style>{S}</style>
        <div className="admin-access-box">加载后台...</div>
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="admin-access">
        <style>{S}</style>
        <div className="admin-access-box">
          <div style={{fontSize:18,fontWeight:850,marginBottom:8}}>无法进入后台</div>
          <div style={{color:'#8f9cad',fontSize:14,marginBottom:16}}>{accessError}</div>
          <button className="admin-btn primary" type="button" onClick={() => nav('/lobby')}>返回大厅</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-root">
      <style>{S}</style>
      <div className="admin-shell">
        <div className="admin-top">
          <div>
            <div className="admin-title">运营后台</div>
            <div className="admin-sub">{user?.nickname || user?.email || '管理员'} · SpaceX Battle</div>
          </div>
          <div className="admin-actions">
            <button className="admin-btn" type="button" onClick={loadSummary}>刷新汇总</button>
            <button className="admin-btn" type="button" onClick={() => nav('/lobby')}>返回大厅</button>
          </div>
        </div>

        <div className="admin-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`admin-tab${active === tab.id ? ' active' : ''}`}
              type="button"
              onClick={() => setActive(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {summary && (
          <div className="admin-metrics">
            <div className="admin-metric">
              <div className="admin-metric-label">总用户</div>
              <div className="admin-metric-value">{fmtInt(summary.total_users)}</div>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-label">今日新增</div>
              <div className="admin-metric-value">{fmtInt(summary.today_new_users)}</div>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-label">今日对局</div>
              <div className="admin-metric-value">{fmtInt(summary.today_rooms)}</div>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-label">待处理提现</div>
              <div className="admin-metric-value">{fmtInt(summary.pending_withdrawals)}</div>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-label">启用任务</div>
              <div className="admin-metric-value">{fmtInt(summary.active_tasks)}</div>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-label">全站 COIN</div>
              <div className="admin-metric-value">{fmtInt(summary.total_points)}</div>
            </div>
          </div>
        )}

        {notice && <div className="admin-notice">{notice}</div>}

        {active === 'daily' && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">每日数据</div>
              <div className="admin-toolbar">
                <div className="admin-field">
                  <label>统计天数</label>
                  <select className="admin-select" value={dailyDays} onChange={e => setDailyDays(Number(e.target.value))}>
                    <option value={7}>7 天</option>
                    <option value={14}>14 天</option>
                    <option value={30}>30 天</option>
                    <option value={60}>60 天</option>
                    <option value={90}>90 天</option>
                  </select>
                </div>
                <button className="admin-btn primary" type="button" onClick={loadDaily}>刷新</button>
              </div>
            </div>
            {dailyRows.length === 0 ? (
              <EmptyState loading={dailyLoading} />
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>日期</th>
                      <th>新增用户</th>
                      <th>活跃用户</th>
                      <th>开局数</th>
                      <th>参与人次</th>
                      <th>爆仓</th>
                      <th>COIN 流入</th>
                      <th>COIN 流出</th>
                      <th>任务点击</th>
                      <th>充值 USDT</th>
                      <th>提现 COIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyRows.map(row => (
                      <tr key={row.day}>
                        <td className="admin-muted">{row.day}</td>
                        <td>{fmtInt(row.new_users)}</td>
                        <td>{fmtInt(row.active_users)}</td>
                        <td>{fmtInt(row.rooms_created)}</td>
                        <td>{fmtInt(row.player_entries)}</td>
                        <td>{fmtInt(row.bust_count)}</td>
                        <td className="admin-kpi-pos">+{fmtInt(row.points_in)}</td>
                        <td className="admin-kpi-neg">{fmtInt(row.points_out)}</td>
                        <td>{fmtInt(row.task_clicks)} / {fmtInt(row.task_click_users)}</td>
                        <td>{fmtDecimal(row.deposit_usdt)}</td>
                        <td>{fmtInt(row.withdrawal_coins)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {active === 'users' && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">全部用户</div>
              <form className="admin-toolbar" onSubmit={e => { e.preventDefault(); setUserPage(1); loadUsers(1); }}>
                <div className="admin-field">
                  <label>搜索</label>
                  <input className="admin-input" value={userQ} onChange={e => setUserQ(e.target.value)} placeholder="ID / 邮箱 / 昵称 / 邀请码" />
                </div>
                <div className="admin-field">
                  <label>状态</label>
                  <select className="admin-select" value={userStatus} onChange={e => { setUserStatus(e.target.value); setUserPage(1); }}>
                    <option value="all">全部</option>
                    <option value="admin">管理员</option>
                    <option value="agent">代理</option>
                    <option value="banned">已封禁</option>
                  </select>
                </div>
                <button className="admin-btn primary" type="submit">查询</button>
              </form>
            </div>
            {userRows.length === 0 ? (
              <EmptyState loading={usersLoading} />
            ) : (
              <>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>用户</th>
                        <th>余额</th>
                        <th>USDT Balance</th>
                        <th>代理</th>
                        <th>邀请</th>
                        <th>状态</th>
                        <th>注册时间</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userRows.map(row => (
                        <tr key={row.id}>
                          <td>#{row.id}</td>
                          <td>
                            <div className="admin-user">
                              <strong>{row.nickname || '未命名'}</strong>
                              <span>{row.email}</span>
                            </div>
                          </td>
                          <td>
                            <div>{fmtInt(row.points)} COIN</div>
                            <div className="admin-muted">{fmtInt(row.cards)} 张房卡</div>
                          </td>
                          <td>
                            <div>{fmtDecimal(row.usdt_balance, 8)} USDT</div>
                            <div className="admin-muted">{fmtDecimal(row.usdt_balance)} USDT</div>
                          </td>
                          <td>
                            <select
                              className="admin-select"
                              value={row.agent_level}
                              onChange={e => patchUser(row, { agentLevel: Number(e.target.value) })}
                              style={{width:92}}
                            >
                              <option value={0}>普通</option>
                              <option value={1}>一级</option>
                              <option value={2}>二级</option>
                            </select>
                          </td>
                          <td>
                            <div>{row.invite_code || '-'}</div>
                            <div className="admin-muted">{fmtInt(row.invite_count)} 人</div>
                          </td>
                          <td>
                            <div className="admin-row-actions">
                              {row.is_admin && <span className="admin-badge gold">管理员</span>}
                              {row.is_banned ? <span className="admin-badge red">封禁</span> : <span className="admin-badge green">正常</span>}
                              {row.passkey_bound_at && <span className="admin-badge blue">Passkey</span>}
                            </div>
                          </td>
                          <td>{fmtDate(row.created_at)}</td>
                          <td>
                            <div className="admin-row-actions">
                              <button
                                className={`admin-btn ${row.is_banned ? 'success' : 'warn'}`}
                                type="button"
                                onClick={() => patchUser(row, { isBanned: !row.is_banned })}
                                disabled={row.id === user?.id}
                              >
                                {row.is_banned ? '解封' : '封禁'}
                              </button>
                              <button
                                className="admin-btn"
                                type="button"
                                onClick={() => patchUser(row, { isAdmin: !row.is_admin })}
                                disabled={row.id === user?.id && row.is_admin}
                              >
                                {row.is_admin ? '撤管' : '设管'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="admin-pager">
                  <button className="admin-btn" type="button" disabled={userPage <= 1} onClick={() => setUserPage(p => Math.max(1, p - 1))}>上一页</button>
                  <span>第 {userPage} / {totalUserPages} 页，共 {fmtInt(userTotal)} 条</span>
                  <button className="admin-btn" type="button" disabled={userPage >= totalUserPages} onClick={() => setUserPage(p => p + 1)}>下一页</button>
                </div>
              </>
            )}
          </section>
        )}

        {active === 'game' && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">游戏数据查询</div>
              <div className="admin-toolbar">
                <div className="admin-segment">
                  <button className={gameMode === 'records' ? 'active' : ''} type="button" onClick={() => { setGameMode('records'); setGamePage(1); }}>对局记录</button>
                  <button className={gameMode === 'ledger' ? 'active' : ''} type="button" onClick={() => { setGameMode('ledger'); setGamePage(1); }}>流水记录</button>
                </div>
              </div>
            </div>
            <form className="admin-panel-head" onSubmit={e => { e.preventDefault(); setGamePage(1); loadGame(1); }}>
              <div className="admin-toolbar">
                <div className="admin-field">
                  <label>用户 ID</label>
                  <input className="admin-input" value={gameFilters.userId} onChange={e => setFilter('userId', e.target.value)} inputMode="numeric" />
                </div>
                <div className="admin-field">
                  <label>房间 ID</label>
                  <input className="admin-input" value={gameFilters.roomId} onChange={e => setFilter('roomId', e.target.value)} inputMode="numeric" />
                </div>
                {gameMode === 'records' ? (
                  <>
                    <div className="admin-field">
                      <label>场次</label>
                      <select className="admin-select" value={gameFilters.level} onChange={e => setFilter('level', e.target.value)}>
                        <option value="">全部</option>
                        <option value="1">初级场</option>
                        <option value="2">中级场</option>
                        <option value="3">高级场</option>
                      </select>
                    </div>
                    <div className="admin-field">
                      <label>房间状态</label>
                      <select className="admin-select" value={gameFilters.status} onChange={e => setFilter('status', e.target.value)}>
                        <option value="">全部</option>
                        {ROOM_STATUS.map(status => <option key={status} value={status}>{status}</option>)}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="admin-field">
                      <label>流水类型</label>
                      <select className="admin-select" value={gameFilters.type} onChange={e => setFilter('type', e.target.value)}>
                        <option value="">全部</option>
                        {LEDGER_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </div>
                    <div className="admin-field">
                      <label>资产</label>
                      <select className="admin-select" value={gameFilters.asset} onChange={e => setFilter('asset', e.target.value)}>
                        <option value="">全部</option>
                        <option value="points">points</option>
                        <option value="card">card</option>
                        <option value="usdt">usdt</option>
                        <option value="usdt">usdt</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="admin-field">
                  <label>开始日期</label>
                  <input className="admin-input" type="date" value={gameFilters.from} onChange={e => setFilter('from', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>结束日期</label>
                  <input className="admin-input" type="date" value={gameFilters.to} onChange={e => setFilter('to', e.target.value)} />
                </div>
              </div>
              <button className="admin-btn primary" type="submit">查询</button>
            </form>

            {gameRows.length === 0 ? (
              <EmptyState loading={gameLoading} />
            ) : gameMode === 'records' ? (
              <>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>房间</th>
                        <th>用户</th>
                        <th>场次</th>
                        <th>状态</th>
                        <th>方向</th>
                        <th>携带 / 最终</th>
                        <th>盈亏 / 瓜分</th>
                        <th>爆仓</th>
                        <th>加入时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gameRows.map(row => (
                        <tr key={row.id}>
                          <td>#{row.room_id}</td>
                          <td>
                            <div className="admin-user">
                              <strong>#{row.user_id} {row.nickname || ''}</strong>
                              <span>{row.email}</span>
                            </div>
                          </td>
                          <td>{row.level} 级</td>
                          <td><span className="admin-badge blue">{row.status}</span></td>
                          <td>{directionText(row.direction)}</td>
                          <td>{fmtInt(row.carry_pts)} / {fmtInt(row.final_pts)}</td>
                          <td>
                            <span className={asNumber(row.profit_pts) >= 0 ? 'admin-kpi-pos' : 'admin-kpi-neg'}>{fmtInt(row.profit_pts)}</span>
                            <span className="admin-muted"> / {fmtInt(row.share_pts)}</span>
                          </td>
                          <td>{row.is_bust ? <span className="admin-badge red">是</span> : <span className="admin-badge green">否</span>}</td>
                          <td>{fmtDate(row.joined_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <GamePager page={gamePage} pages={totalGamePages} total={gameTotal} setPage={setGamePage} />
              </>
            ) : (
              <>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>用户</th>
                        <th>类型</th>
                        <th>资产</th>
                        <th>金额</th>
                        <th>余额</th>
                        <th>房间</th>
                        <th>备注</th>
                        <th>时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gameRows.map(row => {
                        const amount = Number(amountOf(row));
                        return (
                          <tr key={row.id}>
                            <td>#{row.id}</td>
                            <td>
                              <div className="admin-user">
                                <strong>#{row.user_id} {row.nickname || ''}</strong>
                                <span>{row.email}</span>
                              </div>
                            </td>
                            <td>{row.type}</td>
                            <td>{row.asset}</td>
                            <td className={amount >= 0 ? 'admin-kpi-pos' : 'admin-kpi-neg'}>
                              {amount >= 0 ? '+' : ''}{fmtDecimal(amountOf(row), 8)}
                            </td>
                            <td>{fmtDecimal(row.balance_after_decimal ?? row.balance_after, 8)}</td>
                            <td>{row.room_id ? `#${row.room_id}` : '-'}</td>
                            <td className="admin-muted">{row.note || '-'}</td>
                            <td>{fmtDate(row.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <GamePager page={gamePage} pages={totalGamePages} total={gameTotal} setPage={setGamePage} />
              </>
            )}
          </section>
        )}

        {active === 'tasks' && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">任务设置</div>
              <button className="admin-btn" type="button" onClick={resetTaskForm}>新建任务</button>
            </div>
            <div className="admin-task-grid">
              <div className="admin-form-panel">
                <div className="admin-form-title">{taskForm.id ? `编辑任务 #${taskForm.id}` : '新建任务'}</div>
                <form className="admin-form" onSubmit={saveTask}>
                  <div className="admin-field">
                    <label>图标地址</label>
                    <input className="admin-input" value={taskForm.iconUrl} onChange={e => setTaskForm(f => ({ ...f, iconUrl: e.target.value }))} placeholder="/ansem-logo.webp" />
                  </div>
                  <div className="admin-field">
                    <label>任务描述</label>
                    <textarea className="admin-input" value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                  <div className="admin-field">
                    <label>奖励文案</label>
                    <input className="admin-input" value={taskForm.rewardText} onChange={e => setTaskForm(f => ({ ...f, rewardText: e.target.value }))} placeholder="+1000 COIN" />
                  </div>
                  <div className="admin-field">
                    <label>跳转链接</label>
                    <input className="admin-input" value={taskForm.targetUrl} onChange={e => setTaskForm(f => ({ ...f, targetUrl: e.target.value }))} placeholder="https://..." />
                  </div>
                  <div className="admin-field">
                    <label>排序</label>
                    <input className="admin-input" type="number" value={taskForm.sortOrder} onChange={e => setTaskForm(f => ({ ...f, sortOrder: e.target.value }))} />
                  </div>
                  <label className="admin-check">
                    <input type="checkbox" checked={taskForm.isActive} onChange={e => setTaskForm(f => ({ ...f, isActive: e.target.checked }))} />
                    启用任务
                  </label>
                  <div className="admin-row-actions">
                    <button className="admin-btn primary" type="submit" disabled={taskSaving}>{taskSaving ? '保存中...' : '保存任务'}</button>
                    {taskForm.id && <button className="admin-btn" type="button" onClick={resetTaskForm}>取消编辑</button>}
                  </div>
                </form>
              </div>

              <div className="admin-table-wrap">
                {tasks.length === 0 ? (
                  <EmptyState loading={tasksLoading} />
                ) : (
                  <table className="admin-table" style={{minWidth:840}}>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>任务</th>
                        <th>奖励</th>
                        <th>排序</th>
                        <th>点击</th>
                        <th>状态</th>
                        <th>更新时间</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map(row => (
                        <tr key={row.id}>
                          <td>#{row.id}</td>
                          <td>
                            <div className="admin-user">
                              <strong>{row.description}</strong>
                              <span>{row.target_url}</span>
                            </div>
                          </td>
                          <td>{row.reward_text || '-'}</td>
                          <td>{row.sort_order}</td>
                          <td>{fmtInt(row.click_count)} / {fmtInt(row.click_users)}</td>
                          <td>{row.is_active ? <span className="admin-badge green">启用</span> : <span className="admin-badge">停用</span>}</td>
                          <td>{fmtDate(row.updated_at)}</td>
                          <td>
                            <div className="admin-row-actions">
                              <button className="admin-btn" type="button" onClick={() => editTask(row)}>编辑</button>
                              <button className="admin-btn" type="button" onClick={() => toggleTask(row)}>{row.is_active ? '停用' : '启用'}</button>
                              <button className="admin-btn warn" type="button" onClick={() => deleteTask(row)}>删除</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </section>
        )}

        {active === 'bots' && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <div className="admin-panel-title">机器人陪玩配置</div>
                <div className="admin-sub">每个场次可设置 0-{fmtInt(botMax)} 个机器人，玩家进入房间后自动加入陪玩。</div>
              </div>
              <button className="admin-btn" type="button" onClick={loadBots}>刷新</button>
            </div>
            <form onSubmit={saveBots}>
              <div className="admin-bot-grid">
                {[1, 2, 3].map(level => (
                  <div className="admin-bot-card" key={level}>
                    <div className="admin-bot-title">{BOT_LEVEL_LABELS[level]}</div>
                    <div className="admin-bot-desc">设置该场次每个房间的机器人陪玩数量。</div>
                    <div className="admin-field">
                      <label>机器人数量</label>
                      <input
                        className="admin-input"
                        type="number"
                        min={0}
                        max={botMax}
                        value={botCounts[level]}
                        onChange={e => setBotCount(level, e.target.value)}
                        disabled={botLoading || botSaving}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="admin-pager">
                <span>{botLoading ? '加载中...' : '配置保存后对新进入的等待房间生效'}</span>
                <button className="admin-btn primary" type="submit" disabled={botLoading || botSaving}>
                  {botSaving ? '保存中...' : '保存配置'}
                </button>
              </div>
            </form>
          </section>
        )}
        {active === 'notice' && (
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div>
                <div className="admin-panel-title">跑马灯公告</div>
                <div className="admin-sub">设置后立即生效，游戏房间顶部滚动展示。链接为选填，填写后文字可点击跳转。</div>
              </div>
            </div>
            <div className="admin-field" style={{ marginBottom: 16 }}>
              <label>公告文字 <span style={{ color: '#4A6A9A', fontWeight: 400 }}>（最多200字，留空则不显示跑马灯）</span></label>
              <input
                className="admin-input"
                type="text"
                maxLength={200}
                placeholder="例：新手教程：做多=看涨，做空=看跌，1000倍杠杆，60秒定胜负"
                value={noticeText}
                onChange={e => setNoticeText(e.target.value)}
              />
            </div>
            <div className="admin-field" style={{ marginBottom: 20 }}>
              <label>跳转链接 <span style={{ color: '#4A6A9A', fontWeight: 400 }}>（选填，必须以 https:// 开头）</span></label>
              <input
                className="admin-input"
                type="url"
                placeholder="https://..."
                value={noticeUrl}
                onChange={e => setNoticeUrl(e.target.value)}
              />
            </div>
            {noticeText && (
              <div style={{ marginBottom: 20, padding: '8px 0', borderTop: '1px solid #1e2d45', borderBottom: '1px solid #1e2d45' }}>
                <div style={{ fontSize: 11, color: '#4A6A9A', marginBottom: 6 }}>预览效果：</div>
                <div style={{ height: 26, overflow: 'hidden', background: 'rgba(46,127,232,0.10)', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: '#8BA8D4', whiteSpace: 'nowrap', padding: '0 12px' }}>
                    {noticeUrl
                      ? <a href={noticeUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#7EB8FF', textDecoration: 'underline' }}>{noticeText}</a>
                      : noticeText}
                  </div>
                </div>
              </div>
            )}
            <div className="admin-pager">
              <button className="admin-btn" type="button" onClick={() => { setNoticeText(''); setNoticeUrl(''); }}>清空</button>
              <button className="admin-btn primary" type="button" onClick={saveNotice} disabled={noticeSaving}>
                {noticeSaving ? '保存中...' : '保存并发布'}
              </button>
            </div>
          </section>
        )}

        {active === 'redpack' && <RedpackPanel />}

      </div>
    </div>
  );
}

// ─── 房卡红包管理面板 ────────────────────────────────────────────────
function RedpackPanel() {
  const [form, setForm] = useState({ title: '房卡红包', totalCards: '', perClaim: 1, maxClaimers: '', expiresHours: 24 });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { code, link }
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { loadList(); }, []);

  async function loadList() {
    setListLoading(true);
    try {
      const d = await API.get('/api/redpack/list');  // M2 fix: 统一用 API 模块
      setList(d.list || []);
    } catch {} finally { setListLoading(false); }
  }

  async function handleCreate() {
    setErr('');
    if (!form.totalCards || !form.maxClaimers) { setErr('请填写总房卡数和最多抢几人'); return; }
    setCreating(true);
    try {
      const d = await API.post('/api/redpack/create', {  // M2 fix
        title: form.title,
        totalCards: Number(form.totalCards),
        perClaim: Number(form.perClaim),
        maxClaimers: Number(form.maxClaimers),
        expiresHours: Number(form.expiresHours),
      });
      if (!d.ok) throw new Error(d.error || '创建失败');
      setCreated({ code: d.code, link: window.location.origin + d.link });
      loadList();
    } catch (e) { setErr(e.message || '创建失败'); } finally { setCreating(false); }
  }

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <section className="admin-panel">
      <div className="admin-panel-head">
        <div>
          <div className="admin-panel-title">房卡红包</div>
          <div className="admin-sub">创建红包链接，分享给用户，抢到即得房卡，不影响任何游戏逻辑。</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="admin-field">
          <label>红包标题</label>
          <input className="admin-input" value={form.title} onChange={e => f('title', e.target.value)} placeholder="房卡红包" />
        </div>
        <div className="admin-field">
          <label>有效时长（小时）</label>
          <input className="admin-input" type="number" min={1} value={form.expiresHours} onChange={e => f('expiresHours', e.target.value)} />
        </div>
        <div className="admin-field">
          <label>总房卡数</label>
          <input className="admin-input" type="number" min={1} value={form.totalCards} onChange={e => f('totalCards', e.target.value)} placeholder="例：30" />
        </div>
        <div className="admin-field">
          <label>每人得几张</label>
          <input className="admin-input" type="number" min={1} value={form.perClaim} onChange={e => f('perClaim', e.target.value)} />
        </div>
        <div className="admin-field">
          <label>最多几人抢</label>
          <input className="admin-input" type="number" min={1} value={form.maxClaimers} onChange={e => f('maxClaimers', e.target.value)} placeholder="例：10" />
        </div>
      </div>

      {err && <div style={{ color: '#ff6b6b', marginBottom: 12, fontSize: 13 }}>{err}</div>}

      <button className="admin-btn primary" onClick={handleCreate} disabled={creating}>
        {creating ? '创建中...' : '创建红包'}
      </button>

      {created && (
        <div style={{ marginTop: 20, background: 'rgba(245,197,24,0.08)', border: '1px solid rgba(245,197,24,0.3)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#F5C518', fontWeight: 700, marginBottom: 8 }}>红包创建成功！</div>
          <div style={{ color: '#8BA8D4', fontSize: 13, marginBottom: 6 }}>分享链接：</div>
          <div style={{ color: '#fff', fontSize: 13, wordBreak: 'break-all', marginBottom: 10 }}>{created.link}</div>
          <button className="admin-btn" onClick={() => { navigator.clipboard?.writeText(created.link); }}>复制链接</button>
        </div>
      )}

      <div style={{ marginTop: 28, borderTop: '1px solid #1e2d45', paddingTop: 20 }}>
        <div style={{ fontWeight: 600, color: '#8BA8D4', marginBottom: 12 }}>已创建红包</div>
        {listLoading ? <div style={{ color: '#4A6A9A' }}>加载中...</div> : list.length === 0 ? <div style={{ color: '#4A6A9A' }}>暂无红包</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {list.map(rp => {
              const expired = new Date(rp.expires_at) < new Date();
              const finished = rp.claimed_count >= rp.max_claimers;
              const link = `${window.location.origin}/redpack/${rp.code}`;
              return (
                <div key={rp.id} style={{ background: '#0d1b3e', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff', marginBottom: 4 }}>{rp.title}</div>
                    <div style={{ fontSize: 12, color: '#8BA8D4' }}>
                      每人 {rp.per_claim} 张 · {rp.claimed_count}/{rp.max_claimers} 人已抢 ·
                      <span style={{ color: expired || finished ? '#ff6b6b' : '#4caf50', marginLeft: 4 }}>
                        {expired ? '已过期' : finished ? '已抢完' : '进行中'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#4A6A9A', marginTop: 2 }}>到期: {new Date(rp.expires_at).toLocaleString('zh-CN', { hour12: false })}</div>
                  </div>
                  <button className="admin-btn" style={{ flexShrink: 0 }} onClick={() => navigator.clipboard?.writeText(link)}>复制链接</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function GamePager({ page, pages, total, setPage }) {
  return (
    <div className="admin-pager">
      <button className="admin-btn" type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>上一页</button>
      <span>第 {page} / {pages} 页，共 {fmtInt(total)} 条</span>
      <button className="admin-btn" type="button" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>下一页</button>
    </div>
  );
}
